import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import AddressAutocomplete from '../components/AddressAutocomplete';

export default function Checkout() {
  const { cartItems, getCartTotal, clearCart, getCartSource, cartSource } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const urlHubId = searchParams.get('hubId');
  const [loading, setLoading] = useState(false);
  const [vendorName, setVendorName] = useState('');
  const [creditBalances, setCreditBalances] = useState([]);
  const [useCredits, setUseCredits] = useState(false);
  const [creditsToUse, setCreditsToUse] = useState(0);
  const [notes, setNotes] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('cash_on_delivery');
  const [useAltAddress, setUseAltAddress] = useState(false);
  const [updateProfileAddress, setUpdateProfileAddress] = useState(false);

  // Alternative delivery address (only used if useAltAddress is true,
  // or if the user has no verified profile address).
  const [altAddress, setAltAddress] = useState({
    address: '',
    suburb: '',
    state: '',
    postcode: '',
    pid: null,
    streetNumber: '',
    streetName: '',
    streetType: '',
    coordinates: [0, 0],
    source: null
  });

  // Fetch vendor name
  useEffect(() => {
    const fetchVendorName = async () => {
      if (cartSource?.id) {
        try {
          const res = await fetch(`/api/users/${cartSource.id}`);
          const data = await res.json();
          setVendorName(data.farmName || data.name || (cartSource.type === 'hub' ? 'Hub' : 'Producer'));
        } catch (e) {
          setVendorName(cartSource.type === 'hub' ? 'Hub' : 'Producer');
        }
      }
    };
    fetchVendorName();
  }, [cartSource?.id]);

  // Fetch unified credit balances
  useEffect(() => {
    const fetchCreditBalances = async () => {
      try {
        const res = await fetch('/api/credits/balances', {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        const data = await res.json();
        setCreditBalances(data);
      } catch (err) {
        console.error('Failed to fetch credit balances:', err);
      }
    };
    fetchCreditBalances();
  }, []);

  const handleAddressSelect = (result) => {
    setAltAddress({
      address: result.address,
      suburb: result.components.suburb,
      state: result.components.state,
      postcode: result.components.postcode,
      pid: result.pid,
      streetNumber: result.components.number,
      streetName: result.components.streetName,
      streetType: result.components.streetType,
      coordinates: result.coordinates,
      source: 'addresstify'
    });
    setUpdateProfileAddress(true);
  };

  // Build the address that will actually be sent to the backend
  const buildDeliveryAddress = () => {
    const hasProfile = user?.location?.pid && user?.location?.address;

    if (useAltAddress || !hasProfile) {
      // Use the alternative address — must be verified
      return altAddress;
    }

    // Use the profile address
    return {
      address: user.location.address,
      suburb: user.location.suburb,
      state: user.location.state,
      postcode: user.location.postcode,
      pid: user.location.pid,
      streetNumber: user.location.streetNumber || '',
      streetName: user.location.streetName || '',
      streetType: user.location.streetType || '',
      coordinates: user.location.coordinates || [0, 0],
      source: 'user_profile'
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const deliveryAddress = buildDeliveryAddress();

    // Enforce addresstify-verified delivery address
    if (!deliveryAddress.pid) {
      alert('Please select a delivery address from the suggestions.');
      setLoading(false);
      return;
    }

    try {
      const source = getCartSource();
      const finalHubId = (source?.type === 'hub') ? (source?.id || urlHubId) : null;

      let totalAmount = getCartTotal();
      let creditsUsedAmount = 0;

      const currentVendorBalance = creditBalances.find(b => b.issuerId === (source?.id || urlHubId));

      if (currentVendorBalance && useCredits && creditsToUse > 0) {
        creditsUsedAmount = Math.min(creditsToUse, currentVendorBalance.balance, totalAmount);
        totalAmount -= creditsUsedAmount;
      }

      const orderData = {
        products: cartItems.map(item => ({ productId: item.productId, quantity: item.quantity })),
        paymentMethod,
        deliveryAddress,
        notes,
        hubId: finalHubId || null,
        creditsUsed: creditsUsedAmount,
        updateProfileAddress: useAltAddress && updateProfileAddress
      };

      const res = await fetch('/api/orders', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(orderData)
      });
      if (!res.ok) {
        const errBody = await res.text();
        throw new Error(errBody);
      }
      const order = await res.json();

      if (paymentMethod === 'stripe') {
        const payRes = await fetch('/api/payment/create-checkout-session', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({ orderId: order._id })
        });
        const { url } = await payRes.json();
        window.location.href = url;
      } else {
        clearCart();
        navigate(`/order-confirmation/${order._id}`);
      }
    } catch (error) {
      console.error(error);
      alert('Checkout failed: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const total = getCartTotal();
  const currentVendorBalance = creditBalances.find(b => b.issuerId === (cartSource?.id || urlHubId));
  const finalTotal = total - (useCredits && currentVendorBalance ? creditsToUse : 0);
  const hasVerifiedProfileAddress = user?.location?.address && user?.location?.pid;

  if (cartItems.length === 0) {
    navigate('/cart');
    return null;
  }

  return (
    <div className="max-w-4xl mx-auto">
      <h1 className="text-3xl font-bold mb-8">Checkout</h1>

      {/* Vendor indicator */}
      {cartSource && (
        <div className="bg-blue-50 p-3 rounded mb-4 border border-blue-200">
          <p className="text-sm text-blue-700">
            🛒 Shopping from: <strong>{vendorName || (cartSource.type === 'hub' ? 'Hub' : 'Producer')}</strong>
          </p>
          <p className="text-xs text-blue-600 mt-1">
            Your cart contains items from this vendor only.
          </p>
        </div>
      )}

      {/* Credits available from this vendor */}
      {currentVendorBalance && currentVendorBalance.balance > 0 && (
        <div className="bg-green-50 p-3 rounded mb-4 border border-green-200">
          <p className="text-sm text-green-700">
            💰 You have <strong>{currentVendorBalance.balance} credits</strong> available from {currentVendorBalance.issuerName}.
          </p>
          <p className="text-xs text-green-600 mt-1">
            1 credit = $1.00 toward this purchase.
          </p>
          <label className="flex items-center gap-2 mt-2">
            <input
              type="checkbox"
              checked={useCredits}
              onChange={(e) => {
                setUseCredits(e.target.checked);
                if (!e.target.checked) setCreditsToUse(0);
              }}
            />
            <span className="text-sm">Apply credits to this order</span>
          </label>
          {useCredits && (
            <div className="mt-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                How many credits to use?
              </label>
              <div className="flex items-center gap-3">
                <input
                  type="number"
                  min="0"
                  max={currentVendorBalance.balance}
                  step="1"
                  value={creditsToUse}
                  onChange={(e) => {
                    let value = parseInt(e.target.value) || 0;
                    if (value > currentVendorBalance.balance) value = currentVendorBalance.balance;
                    if (value < 0) value = 0;
                    setCreditsToUse(value);
                  }}
                  className="border rounded px-3 py-2 w-32 text-center"
                />
                <button
                  type="button"
                  onClick={() => {
                    const maxToUse = Math.min(currentVendorBalance.balance, getCartTotal());
                    setCreditsToUse(maxToUse);
                  }}
                  className="text-sm bg-green-100 text-green-700 px-3 py-2 rounded hover:bg-green-200"
                >
                  Use Max
                </button>
              </div>
              <div className="mt-2">
                <p className="text-sm">
                  Order total: <strong>${getCartTotal().toFixed(2)}</strong>
                  {creditsToUse > 0 && (
                    <>
                      <br />
                      Credits applied: <strong>-${creditsToUse.toFixed(2)}</strong>
                      <br />
                      <span className="text-green-600 font-bold">
                        New total: ${Math.max(0, getCartTotal() - creditsToUse).toFixed(2)}
                      </span>
                    </>
                  )}
                </p>
                {creditsToUse > getCartTotal() && (
                  <p className="text-xs text-red-500 mt-1">
                    Note: Only {getCartTotal()} credits will be used (excess will be ignored).
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4 bg-white p-6 rounded shadow">
        {/* Delivery address section */}
        <div>
          <label className="block text-gray-700 mb-2 font-semibold">Delivery Address *</label>

          {hasVerifiedProfileAddress && !useAltAddress ? (
            <>
              {/* Show saved profile address as read-only */}
              <div className="bg-gray-50 p-3 rounded border border-gray-200">
                <p className="text-sm text-gray-800">
                  <strong>{user.location.address}</strong>
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {user.location.suburb} {user.location.state} {user.location.postcode}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setUseAltAddress(true)}
                className="text-sm text-blue-600 hover:text-blue-800 mt-2"
              >
                Deliver to a different address
              </button>
            </>
          ) : (
            <>
              {/* Autocomplete for new / different address */}
              {!hasVerifiedProfileAddress && (
                <p className="text-sm text-amber-600 mb-2">
                  You don't have a saved delivery address yet. Please enter one below.
                </p>
              )}

              <AddressAutocomplete
                value={altAddress.address}
                onChange={(text) => setAltAddress(prev => ({ ...prev, address: text, pid: null }))}
                onSelect={handleAddressSelect}
                placeholder="Start typing the delivery address..."
                required
              />

              {altAddress.pid && (
                <p className="text-xs text-gray-500 mt-1">
                  ✓ {altAddress.suburb} {altAddress.state} {altAddress.postcode}
                </p>
              )}
              {!altAddress.pid && altAddress.address && (
                <p className="text-xs text-amber-600 mt-1">
                  Please select an address from the suggestions.
                </p>
              )}

              {/* Update profile checkbox — only show if we're actually changing */}
              {hasVerifiedProfileAddress && altAddress.pid &&
               altAddress.address !== user.location.address && (
                <div className="mt-2">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={updateProfileAddress}
                      onChange={(e) => setUpdateProfileAddress(e.target.checked)}
                      className="w-4 h-4"
                    />
                    <span className="text-sm text-gray-600">
                      Update my profile address with this delivery address
                    </span>
                  </label>
                </div>
              )}

              {/* Cancel switch back to profile address */}
              {hasVerifiedProfileAddress && (
                <button
                  type="button"
                  onClick={() => {
                    setUseAltAddress(false);
                    setAltAddress({
                      address: '', suburb: '', state: '', postcode: '',
                      pid: null, streetNumber: '', streetName: '', streetType: '',
                      coordinates: [0, 0], source: null
                    });
                    setUpdateProfileAddress(false);
                  }}
                  className="text-sm text-blue-600 hover:text-blue-800 mt-2"
                >
                  ← Use my saved address instead
                </button>
              )}
            </>
          )}
        </div>

        <div>
          <label className="block text-gray-700 mb-2">Payment Method *</label>
          <select
            value={paymentMethod}
            onChange={(e) => setPaymentMethod(e.target.value)}
            required
            className="w-full border p-2"
          >
            <option value="cash_on_delivery">Cash on Delivery</option>
            <option value="bank_transfer">Bank Transfer</option>
            <option value="stripe">Credit Card (Stripe)</option>
          </select>
        </div>

        <div>
          <label className="block text-gray-700 mb-2">Order Notes (Optional)</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows="3"
            className="w-full border p-2"
          />
        </div>

        <div className="border-t pt-4 mt-4">
          <div className="space-y-2">
            <div className="flex justify-between">
              <span>Subtotal</span>
              <span>${getCartTotal().toFixed(2)}</span>
            </div>
            {creditsToUse > 0 && (
              <div className="flex justify-between text-green-600">
                <span>Credits applied</span>
                <span>-${creditsToUse.toFixed(2)}</span>
              </div>
            )}
            <div className="flex justify-between text-lg font-bold">
              <span>Total to pay</span>
              <span className="text-green-600">${Math.max(0, getCartTotal() - creditsToUse).toFixed(2)}</span>
            </div>
          </div>
        </div>

        <button type="submit" disabled={loading} className="w-full bg-green-600 text-white py-3 rounded">
          {loading ? 'Processing...' : `Place Order ($${finalTotal.toFixed(2)})`}
        </button>
      </form>
    </div>
  );
}
